/**
 * HeroLoadingScreen — shown while an authenticated user's tabletop is
 * connecting, i.e. after the launch JWT resolves but before the server's
 * `session_state` arrives (the window where the old "Test Arena" default
 * map used to flash).
 *
 * Backdrop is the game's hero/banner image (the same art as the
 * landing-page card), darkened for legibility. Falls back to the neutral
 * dark surface when no image is set, or before session metadata loads.
 */
export interface HeroLoadingScreenProps {
  /** Preferred hero art; falls back to the game banner, then a flat surface. */
  heroImage?: string | null
  gameImage?: string | null
  gameTitle?: string
}

export function HeroLoadingScreen({
  heroImage,
  gameImage,
  gameTitle,
}: HeroLoadingScreenProps) {
  const image = heroImage || gameImage || null

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
        background: 'hsl(var(--ac-surface-0))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Hero art backdrop — cover, fades in once provided. */}
      {image && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url("${image}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            // Darken for legibility of the foreground text/spinner.
            filter: 'brightness(0.4) saturate(0.9)',
            transition: 'opacity var(--ac-dur-base) var(--ac-ease-out)',
          }}
        />
      )}
      {/* Vignette so the centered content stays readable over busy art. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 70% 60% at 50% 50%, transparent, hsl(var(--ac-surface-0) / 0.85))',
        }}
      />

      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1.25rem',
          textAlign: 'center',
          padding: '0 1.5rem',
        }}
      >
        {gameTitle && (
          <h1
            className="font-serif-ac"
            style={{
              margin: 0,
              fontSize: 'clamp(1.75rem, 4vw, 2.75rem)',
              lineHeight: 1.1,
              color: 'hsl(var(--ac-text-light))',
              textShadow: '0 2px 24px hsl(var(--ac-surface-0) / 0.9)',
            }}
          >
            {gameTitle}
          </h1>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {/* Spinner — Tailwind animate-spin, mint accent ring. */}
          <span
            className="animate-spin"
            style={{
              width: '1.25rem',
              height: '1.25rem',
              borderRadius: '9999px',
              border: '2px solid hsl(var(--ac-mint-primary) / 0.25)',
              borderTopColor: 'hsl(var(--ac-mint-primary))',
            }}
          />
          <span
            className="font-script"
            style={{
              fontSize: '0.8125rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'hsl(var(--ac-text-muted))',
            }}
          >
            Entering the tabletop…
          </span>
        </div>
      </div>
    </div>
  )
}
